import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { UserSecurity, UserSessions, WorkOsWidgets } from '@workos-inc/widgets'
import { ArrowLeft } from 'lucide-react'

import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { useMe } from '../../auth/hooks/useMe'
import { useWidgetToken } from '../../auth/hooks/useWidgetToken'
import { lightenColor, darkenColor } from '../../utils/color'

function buildAccentOverrides(hex: string): React.CSSProperties {
  return {
    '--accent-1': lightenColor(hex, 95),
    '--accent-2': lightenColor(hex, 90),
    '--accent-3': lightenColor(hex, 80),
    '--accent-4': lightenColor(hex, 70),
    '--accent-5': lightenColor(hex, 60),
    '--accent-6': lightenColor(hex, 45),
    '--accent-7': lightenColor(hex, 30),
    '--accent-8': lightenColor(hex, 15),
    '--accent-9': hex,
    '--accent-10': darkenColor(hex, 8),
    '--accent-11': darkenColor(hex, 20),
    '--accent-12': darkenColor(hex, 35),
    '--accent-a9': hex,
    '--accent-a10': darkenColor(hex, 8),
    '--accent-contrast': '#fff',
    '--woswidgets-accent-color': hex,
  } as React.CSSProperties
}

export function EngineerSecurityPage() {
  const getSecurityToken = useWidgetToken([])
  const getSessionsToken = useWidgetToken([])
  const navigate = useNavigate()
  const { tenantSlug } = useParams()

  const { data: meData } = useMe()

  // SSO users' passwords/MFA are owned by their identity provider, so the
  // WorkOS Password & MFA controls are hidden for them. Active Sessions stays
  // visible — it's useful regardless of how they authenticate.
  const isSsoManaged = meData?.is_sso_managed ?? false

  const brandColor = meData?.tenant?.primary_color || '#3b82f6'
  const accentOverrides = useMemo(() => buildAccentOverrides(brandColor), [brandColor])

  return (
    <EngineerLayout hideHeader>
      <div className="px-4 pt-4 pb-2">
        <button
          onClick={() => navigate(`/${tenantSlug}/engineer/more`)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      <div className="px-4 pb-4">
        <h1 className="text-xl font-semibold text-gray-900">Security</h1>
        <p className="text-sm text-gray-500 mt-1">
          {isSsoManaged
            ? 'Manage your active sessions. Your password is managed by your organisation.'
            : 'Manage your password and multi-factor authentication.'}
        </p>
      </div>

      <div className="px-4 pb-6 space-y-4">
        {!isSsoManaged && (
          <div className="bg-white rounded-lg border border-gray-200 p-4" style={accentOverrides}>
            <h2 className="text-sm font-medium text-gray-900 mb-3">Password & MFA</h2>
            <WorkOsWidgets>
              <UserSecurity authToken={getSecurityToken} />
            </WorkOsWidgets>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-4" style={accentOverrides}>
          <h2 className="text-sm font-medium text-gray-900 mb-3">Active sessions</h2>
          <WorkOsWidgets>
            <UserSessions authToken={getSessionsToken} />
          </WorkOsWidgets>
        </div>
      </div>
    </EngineerLayout>
  )
}
